import {
  SCHEDULE_FIELDS,
  SCHEDULE_TIME_SLOTS,
  specialBeerDutyLabel,
} from "../../shared/domain/schedule";
import type { BeerDuty } from "../../shared/domain/schedule";

interface ScheduleSortEntry {
  id: string;
  scheduledDate: string;
  timeSlot: string | null;
  status: "scheduled" | "active" | "completed";
}

export { SCHEDULE_FIELDS, SCHEDULE_TIME_SLOTS };

export function timeSlotLabel(value: string | null | undefined): string {
  return SCHEDULE_TIME_SLOTS.find((slot) => slot.value === value)?.label ?? "—";
}

export function timeSlotName(value: string | null | undefined): string {
  return SCHEDULE_TIME_SLOTS.find((slot) => slot.value === value)?.name ?? "—";
}

export function beerDutyLabel(value: BeerDuty | null | undefined): string {
  if (!value) return "Not assigned";
  return value.kind === "player"
    ? value.nameSnapshot
    : specialBeerDutyLabel(value.kind);
}

export function homeAwayLabel(isHome: boolean | undefined): string {
  return isHome ? "Home" : "Away";
}

export function formatScheduleDate(value: string | null | undefined): string {
  if (!value) return "Date unknown";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function sortSchedule<Entry extends ScheduleSortEntry>(
  entries: Entry[],
): Entry[] {
  return [...entries].sort((left, right) =>
    `${left.scheduledDate}|${left.timeSlot}|${left.id}`.localeCompare(
      `${right.scheduledDate}|${right.timeSlot}|${right.id}`,
    ),
  );
}

export function splitSchedule<Entry extends ScheduleSortEntry>(
  entries: Entry[],
  today = new Date().toISOString().slice(0, 10),
): {
  upcoming: Entry[];
  results: Entry[];
} {
  const upcoming = sortSchedule(
    entries.filter((entry) => entry.status !== "completed"),
  );
  const results = sortSchedule(
    entries.filter((entry) => entry.status === "completed"),
  ).reverse();
  return {
    upcoming: upcoming.filter(
      (entry) => entry.scheduledDate >= today || entry.status === "active",
    ),
    results,
  };
}

interface ScheduleResultGame {
  forfeitWinner?: "us" | "them" | null;
  state?: {
    ourRunsByInning?: number[];
    opponentRunsByInning?: number[];
  };
}

export function resultForGame(game: ScheduleResultGame | null): string {
  if (!game) return "";
  if (game.forfeitWinner === "us") return "W 7–0 · Forfeit";
  if (game.forfeitWinner === "them") return "L 0–7 · Forfeit";
  const us = (game.state?.ourRunsByInning ?? []).reduce(
    (sum, value) => sum + value,
    0,
  );
  const them = (game.state?.opponentRunsByInning ?? []).reduce(
    (sum, value) => sum + value,
    0,
  );
  return `${us > them ? "W" : us < them ? "L" : "T"} ${us}–${them}`;
}
